import { getMessaging, getToken, onMessage } from "firebase/messaging";
import AppConfig from './app-config';
import apiClient from './api-client';
import AuthService from './auth';

class NotificationService {
  constructor() {
    this.messaging = null;
    this.currentToken = null;
    this.tokenRefreshListener = null;
    this.messagingSwRegistration = null;
    this.visibilityHandlerBound = false;
    // Named bound handler so it can be removed in unregisterToken()
    this._onVisibilityChange = null;
  }

  async getMessagingServiceWorkerRegistration() {
    if (this.messagingSwRegistration) {
      return this.messagingSwRegistration;
    }

    if (!(typeof window !== 'undefined' && 'serviceWorker' in navigator)) {
      return null;
    }

    try {
      this.messagingSwRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
        scope: '/firebase-cloud-messaging-push-scope',
        updateViaCache: 'none'
      });
      return this.messagingSwRegistration;
    } catch (error) {
      console.warn('Failed to register firebase messaging service worker:', error);
      return null;
    }
  }

  /**
   * Initialize Firebase messaging and set up token refresh listener
   */
  async initialize() {
    if (!('Notification' in window)) {
      console.warn('Notifications not supported in this browser');
      return false;
    }

    try {
      this.messaging = getMessaging();
      
      // Set up message listener for foreground notifications
      onMessage(this.messaging, (payload) => {
        console.log('Foreground notification received:', payload);
        this.handleForegroundNotification(payload);
      });

      // Permission state sync (Milestone 2): if localStorage says notifications
      // are enabled but the user revoked permission in browser settings, mirror
      // that locally so the Profile toggle shows OFF.
      if (AppConfig.getLocal('notification_enable') === true && Notification.permission === 'denied') {
        AppConfig.storeLocal('notification_enable', 'false');
      }

      // Startup token verification + session re-registration (Milestone 1):
      // If permission is granted and the user is logged in, force a re-POST.
      // The backend upsert is idempotent, so one extra POST per page load is
      // safe and heals both token-rotation and backend-purge scenarios.
      if (Notification.permission === 'granted' && AuthService.isLogged()) {
        try {
          await this.registerToken(true);
        } catch (error) {
          // A failed POST must not break app startup.
          console.warn('Startup token re-registration failed:', error);
        }
      } else if (Notification.permission === 'granted'
                 && !AppConfig.getLocal('notification_token_id')
                 && AuthService.isLogged()) {
        // Permission granted but no token registered yet and user is logged in.
        try {
          await this.registerToken(false);
        } catch (error) {
          console.warn('Startup token registration failed:', error);
        }
      }

      // Check if we have an existing token and it's still valid
      const storedTokenId = AppConfig.getLocal('notification_token_id');
      if (storedTokenId) {
        // Token exists, set up refresh monitoring
        this.setupTokenRefreshMonitoring();
      }

      return true;
    } catch (error) {
      console.error('Error initializing notification service:', error);
      return false;
    }
  }

  /**
   * Request notification permission and register token
   */
  async requestPermissionAndRegister() {
    if (!('Notification' in window)) {
      throw new Error('Notifications not supported');
    }

    try {
      const permission = await Notification.requestPermission();
      
      if (permission === 'granted') {
        console.log('Notification permission granted');
        await this.registerToken();
        return true;
      } else if (permission === 'denied') {
        console.log('Notification permission denied');
        return false;
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      throw error;
    }
  }

  /**
   * Register or refresh FCM token with backend
   */
  async registerToken(forceRefresh = false) {
    if (!this.messaging) {
      await this.initialize();
    }

    try {
      // Wait for service worker to be ready
      if ('serviceWorker' in navigator) {
        await navigator.serviceWorker.ready;
      }

      const messagingSwRegistration = await this.getMessagingServiceWorkerRegistration();

      const token = await getToken(this.messaging, { 
        vapidKey: process.env.REACT_APP_VAPID_KEY,
        ...(messagingSwRegistration ? { serviceWorkerRegistration: messagingSwRegistration } : {})
      });

      if (!token) {
        throw new Error('Failed to get FCM token');
      }

      const tokenId = token.slice(-6);
      const storedTokenId = AppConfig.getLocal('notification_token_id');

      // Only register if token is new or force refresh is requested
      if (forceRefresh || tokenId !== storedTokenId) {
        console.log('Registering new token with backend');
        
        const response = await apiClient.post('users/notification/register', {
          token: token,
          platform: AppConfig.getDevicePlatform(),
          device: navigator.userAgent
        }, {}, { action: 'register_notification_token' });

        if (response.status === 200) {
          AppConfig.storeLocal('notification_token_id', tokenId);
          AppConfig.storeLocal('notification_enable', 'true');
          AppConfig.storeLocal('notification_token_timestamp', Date.now());
          this.currentToken = token;
          
          // Set up token refresh monitoring
          this.setupTokenRefreshMonitoring();
          
          console.log('Token registered successfully');
          return true;
        } else {
          console.warn(`Notification token registration returned non-200 status: ${response.status}`);
        }
      } else {
        console.log('Token already registered');
        this.currentToken = token;
        return true;
      }
    } catch (error) {
      console.error('Error registering token:', error);
      throw error;
    }
  }

  /**
   * Set up monitoring for token refresh
   * FCM tokens can be refreshed by Firebase, we need to detect and update
   */
  setupTokenRefreshMonitoring() {
    // Check token validity periodically (every 24 hours)
    if (this.tokenRefreshListener) {
      clearInterval(this.tokenRefreshListener);
    }

    this.tokenRefreshListener = setInterval(async () => {
      // Gate on login to avoid 401 noise on login/public pages
      if (!AuthService.isLogged()) {
        return;
      }
      try {
        await this.checkAndRefreshToken();
      } catch (error) {
        console.error('Error in token refresh check:', error);
      }
    }, 24 * 60 * 60 * 1000); // 24 hours

    // Also check on page visibility change (when user returns to app).
    // Use a named bound handler stored on the instance so it can be removed
    // in unregisterToken() (previously an inline anonymous function).
    if (!this.visibilityHandlerBound) {
      this._onVisibilityChange = async () => {
        if (document.hidden) {
          return;
        }
        // Respect login state to avoid 401 noise on public pages
        if (!AuthService.isLogged()) {
          return;
        }
        try {
          await this.checkAndRefreshToken();
        } catch (error) {
          console.error('Error checking token on visibility change:', error);
        }
      };
      document.addEventListener('visibilitychange', this._onVisibilityChange);
      this.visibilityHandlerBound = true;
    }
  }

  readCandidateUrl(source) {
    if (!source) {
      return null;
    }

    if (typeof source === 'string') {
      return source;
    }

    const candidates = [
      source.url,
      source.link,
      source.click_action,
      source.clickAction,
      source.path,
      source.route,
    ];

    for (const candidate of candidates) {
      if (typeof candidate === 'string' && candidate.trim()) {
        return candidate;
      }
    }

    return null;
  }

  resolveNotificationUrl(data, payload) {
    const directUrl = this.readCandidateUrl(data);
    if (directUrl) {
      return directUrl;
    }

    const notificationDataUrl = this.readCandidateUrl(payload?.notification?.data);
    if (notificationDataUrl) {
      return notificationDataUrl;
    }

    const notificationUrl = this.readCandidateUrl(payload?.notification);
    if (notificationUrl) {
      return notificationUrl;
    }

    const optionsUrl = this.readCandidateUrl(payload?.fcmOptions);
    if (optionsUrl) {
      try {
        const url = new URL(optionsUrl, window.location.origin);
        return `${url.pathname}${url.search}${url.hash}`;
      } catch {
        return optionsUrl;
      }
    }

    return null;
  }

  /**
   * Check if token needs refresh and update if necessary.
   *
   * The token-suffix diff in registerToken() makes registerToken(false) cheap
   * (getToken() is local), so we call it unconditionally to heal token
   * rotation. A periodic forced re-POST (every 7 days) acts as cheap insurance
   * for backend drift (e.g. token purged server-side).
   */
  async checkAndRefreshToken() {
    const lastUpdate = AppConfig.getLocal('notification_token_timestamp');
    const now = Date.now();
    const FORCE_REFRESH_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

    const forceRefresh = !lastUpdate || (now - lastUpdate) > FORCE_REFRESH_MS;

    if (forceRefresh) {
      console.log('Token is older than 7 days (or no timestamp), forcing re-POST...');
    }

    try {
      await this.registerToken(forceRefresh);
    } catch (error) {
      console.error('Failed to refresh token:', error);
    }
  }

  /**
   * Unregister notification token
   */
  async unregisterToken() {
    const tokenId = AppConfig.getLocal('notification_token_id');
    
    if (!tokenId) {
      console.log('No token to unregister');
      // Still tear down any listeners/timers that may be active.
      this._teardownMonitoring();
      return true;
    }

    try {
      const response = await apiClient.post('users/notification/unregister', {
        token_id: tokenId
      }, {}, { action: 'unregister_notification_token' });

      if (response.status === 200) {
        AppConfig.storeLocal('notification_enable', false);
        AppConfig.storeLocal('notification_token_id', '');
        AppConfig.storeLocal('notification_token_timestamp', '');
        this.currentToken = null;
        
        // Clear refresh listener and visibility listener
        this._teardownMonitoring();
        
        console.log('Token unregistered successfully');
        return true;
      }
    } catch (error) {
      console.error('Error unregistering token:', error);
      // Still clear local storage even if backend call fails
      AppConfig.storeLocal('notification_enable', false);
      AppConfig.storeLocal('notification_token_id', '');
      AppConfig.storeLocal('notification_token_timestamp', '');
      this._teardownMonitoring();
      throw error;
    }
  }

  /**
   * Tear down the token refresh interval and the visibilitychange listener.
   * Centralized so unregisterToken() (and the no-token early return) both
   * clean up consistently.
   */
  _teardownMonitoring() {
    if (this.tokenRefreshListener) {
      clearInterval(this.tokenRefreshListener);
      this.tokenRefreshListener = null;
    }
    if (this._onVisibilityChange) {
      document.removeEventListener('visibilitychange', this._onVisibilityChange);
      this._onVisibilityChange = null;
    }
    this.visibilityHandlerBound = false;
  }

  /**
   * Handle foreground notifications
   */
  handleForegroundNotification(payload) {
    const { notification, data } = payload;
    const targetUrl = this.resolveNotificationUrl(data, payload);
    
    if (!notification) return;

    // Show browser notification if permission is granted
    if (Notification.permission === 'granted') {
      const notificationOptions = {
        body: notification.body,
        icon: notification.icon || '/logo.svg',
        badge: notification.badge || '/logo.svg',
        tag: notification.tag || 'default',
        data: data,
        requireInteraction: false
      };

      const browserNotification = new Notification(notification.title, notificationOptions);

      // Handle notification click
      browserNotification.onclick = (event) => {
        event.preventDefault();
        if (targetUrl) {
          window.location.href = targetUrl;
        }
        browserNotification.close();
      };
    }
  }

  /**
   * Check if notifications are enabled
   */
  isEnabled() {
    const val = AppConfig.getLocal('notification_enable');
    return val === true || val === 'true';
  }

  /**
   * Get current token ID
   */
  getTokenId() {
    return AppConfig.getLocal('notification_token_id');
  }
}

// Export singleton instance
const notificationService = new NotificationService();
export default notificationService;
